import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import * as Lucide from "lucide-react";
import { Check } from "lucide-react";
import { useSeo } from "@/lib/useSeo";
import { PageHero, SectionLabel } from "@/components/site/Bits";
import { Reveal, RevealStagger, RevealItem } from "@/components/site/Reveal";
import { VALUES, UNIVERSITIES, IMAGES } from "@/data/content";

function Icon({ name, className }) {
  const C = Lucide[name] || Lucide.Circle;
  return <C className={className} />;
}

function StorySection() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);

  return (
    <section ref={ref} className="bg-white py-24 lg:py-32" data-testid="about-story">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-12 gap-14 items-start">
        <div className="lg:col-span-6">
          <Reveal><SectionLabel>Our story</SectionLabel></Reveal>
          <Reveal delay={0.05}>
            <h2 className="mt-5 font-outfit font-extrabold text-[#0A1F44] text-3xl lg:text-5xl leading-[1.05] tracking-tight">
              Built around the problem no one else was solving
            </h2>
          </Reveal>
          <div className="mt-8 space-y-5 text-[#12294D]/75 leading-relaxed font-manrope">
            <Reveal delay={0.1}><p>Southampton has two universities with a combined enrolment of over 33,000 candidates, thousands of whom are international candidates seeking work opportunities. The city's hospitality, retail, logistics, and healthcare sectors have persistent, structural demand for flexible, motivated workers across all role types.</p></Reveal>
            <Reveal delay={0.14}><p>The connection between these two realities should have been straightforward. It was not — because the compliance complexity of placing international candidates was keeping most agencies at arm's length. Right to Work checks, BRP verification, CAS letters, and work-right guidance are not complicated when you understand them. But for generalist agencies, they represented a liability they were not equipped to manage.</p></Reveal>
            <Reveal delay={0.18}><p>UK Student Job was founded to close that gap. We built the compliance infrastructure and candidate pipeline first — and the partnership model around it. The result is a service that works for job consultancies across Southampton and London, delivering genuine professional opportunities for the international candidates we serve.</p></Reveal>
          </div>
        </div>
        <div className="lg:col-span-6">
          <div className="grid grid-cols-2 gap-4">
            <div className="rounded-2xl overflow-hidden shadow-lg aspect-[3/4] mt-10">
              <motion.img style={{ y }} src={IMAGES.campus} alt="International students on campus" className="w-full h-[116%] object-cover -mt-[8%]" />
            </div>
            <div className="rounded-2xl overflow-hidden shadow-lg aspect-[3/4]">
              <motion.img style={{ y }} src={IMAGES.team} alt="Consultancy team meeting" className="w-full h-[116%] object-cover -mt-[8%]" />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default function About() {
  useSeo(
    "About UK Student Job — The specialist B2B partner for compliant international candidate employment",
    "UK Student Job is the specialist B2B partner for compliant international candidate employment across Southampton and London. Founded to solve a real market gap in international student placement."
  );

  return (
    <>
      <PageHero
        testId="about-hero"
        label="About UK Student Job"
        lines={[<>The specialist B2B partner</>, <>for compliant international</>, <span key="r" className="text-[#C8102E]">candidate employment</span>]}
        sub="Founded to solve a real market gap — and built on the belief that compliance and quality are not trade-offs. They are the only way to operate."
      />

      <StorySection />

      {/* Mission & Vision */}
      <section className="bg-[#F4F6F9] py-24 lg:py-32" data-testid="about-mission-vision">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid md:grid-cols-2 gap-6">
          <Reveal>
            <div className="h-full bg-white rounded-2xl p-10 border border-[#0A1F44]/8 shadow-[0_8px_32px_rgba(10,31,68,0.05)]">
              <div className="inline-flex h-14 w-14 items-center justify-center rounded-xl bg-[#C8102E]/10 text-[#C8102E]">
                <Lucide.Target className="h-7 w-7" />
              </div>
              <h3 className="mt-6 font-outfit font-extrabold text-2xl text-[#0A1F44]">Mission</h3>
              <p className="mt-4 text-[#12294D]/75 leading-relaxed font-manrope">
                We connect an abundant pool of motivated international candidates with the job
                consultancies that need them — making compliant employment effortless for every
                consultancy partner we serve across Southampton and London, and professionally
                meaningful for every candidate we place.
              </p>
            </div>
          </Reveal>
          <Reveal delay={0.08}>
            <div className="h-full bg-[#0A1F44] rounded-2xl p-10 relative overflow-hidden grain">
              <div className="absolute inset-0 flag-stripes opacity-30" />
              <div className="relative">
                <div className="inline-flex h-14 w-14 items-center justify-center rounded-xl bg-white/10 text-white">
                  <Lucide.Telescope className="h-7 w-7" />
                </div>
                <h3 className="mt-6 font-outfit font-extrabold text-2xl text-white">Vision</h3>
                <p className="mt-4 text-white/75 leading-relaxed font-manrope">
                  To become the United Kingdom's most trusted specialist in international candidate
                  employment — the first name that job consultancies turn to when they need a
                  motivated, compliance-managed talent pool across Southampton, London, and beyond.
                </p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Values */}
      <section className="bg-white py-24 lg:py-32" data-testid="about-values">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className="max-w-3xl">
            <Reveal><SectionLabel>Our values</SectionLabel></Reveal>
            <Reveal delay={0.05}>
              <h2 className="mt-5 font-outfit font-extrabold text-[#0A1F44] text-3xl lg:text-5xl tracking-tight">
                The six values we operate by
              </h2>
            </Reveal>
          </div>
          <RevealStagger className="mt-14 grid md:grid-cols-2 lg:grid-cols-3 gap-6" stagger={0.07}>
            {VALUES.map((v) => (
              <RevealItem key={v.title}>
                <div className="group h-full bg-[#F4F6F9] rounded-2xl p-8 border border-[#0A1F44]/8 hover:-translate-y-2 transition-transform duration-500">
                  <div className="inline-flex h-14 w-14 items-center justify-center rounded-xl bg-[#0A1F44] text-white group-hover:bg-[#C8102E] transition-colors duration-300">
                    <Icon name={v.icon} className="h-7 w-7" />
                  </div>
                  <h3 className="mt-6 font-outfit font-bold text-xl text-[#0A1F44]">{v.title}</h3>
                  <p className="mt-3 text-[#12294D]/70 text-sm leading-relaxed font-manrope">{v.body}</p>
                </div>
              </RevealItem>
            ))}
          </RevealStagger>
        </div>
      </section>

      {/* University partners */}
      <section className="bg-[#F4F6F9] py-24 lg:py-32" data-testid="about-universities">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className="max-w-3xl">
            <Reveal><SectionLabel>Our source universities</SectionLabel></Reveal>
            <Reveal delay={0.05}>
              <h2 className="mt-5 font-outfit font-extrabold text-[#0A1F44] text-3xl lg:text-5xl tracking-tight">
                Two universities. One abundant talent pool. Two cities.
              </h2>
            </Reveal>
          </div>
          <RevealStagger className="mt-14 grid md:grid-cols-2 gap-6">
            {UNIVERSITIES.map((u) => (
              <RevealItem key={u.name}>
                <div className="h-full bg-white rounded-2xl p-9 border border-[#0A1F44]/8 shadow-[0_8px_32px_rgba(10,31,68,0.05)]">
                  <div className="flex items-start justify-between">
                    <div className="inline-flex items-center justify-center h-14 w-14 rounded-xl bg-[#0A1F44] text-white">
                      <Lucide.Building2 className="h-7 w-7" />
                    </div>
                    <span className="text-xs font-semibold uppercase tracking-wider text-[#C8102E] text-right max-w-[10rem]">{u.tag}</span>
                  </div>
                  <h3 className="mt-6 font-outfit font-bold text-2xl text-[#0A1F44]">{u.name}</h3>
                  <ul className="mt-5 space-y-3">
                    {u.points.map((p) => (
                      <li key={p} className="flex items-start gap-3 text-sm text-[#12294D]/75 font-manrope">
                        <Check className="h-4 w-4 text-[#C8102E] mt-0.5 shrink-0" /> {p}
                      </li>
                    ))}
                  </ul>
                </div>
              </RevealItem>
            ))}
          </RevealStagger>
          <Reveal delay={0.1}>
            <p className="mt-8 text-[#12294D]/60 font-manrope max-w-3xl">
              International candidates from both universities are placed with consultancy partners
              across Southampton and London — in part-time, seasonal, full-time, fixed-term, and
              permanent roles.
            </p>
          </Reveal>
        </div>
      </section>
    </>
  );
}
